import { useMemo, useRef, useState } from "react";
import type { InviteView } from "@/lib/store";

/**
 * 一次最多列幾筆建議。
 *
 * 接龍名單動輒上百人，全部畫出來的話下拉選單會長到蓋住整個表單，
 * 而且「往下捲一百筆找自己」比直接把名字打完還慢。列前幾筆 + 提示還有多少，
 * 使用者自然會多打一個字把範圍縮掉。
 */
const MAX_SUGGESTIONS = 8;

/** 這筆接龍名額還能報幾位 */
export function remainingOf(i: InviteView): number {
  return Math.max(0, i.quota - i.used);
}

/**
 * 依輸入內容篩接龍名單。開頭吻合的排前面 ——
 * 打「小」的時候，「小火龍」應該排在「阿小」前面。
 */
export function matchInvites(invites: InviteView[], query: string): InviteView[] {
  const q = query.trim().toLowerCase();
  if (!q) return invites;
  const starts: InviteView[] = [];
  const contains: InviteView[] = [];
  for (const i of invites) {
    const name = i.name.toLowerCase();
    if (name.startsWith(q)) starts.push(i);
    else if (name.includes(q)) contains.push(i);
  }
  return [...starts, ...contains];
}

/**
 * 接龍名單專用的姓名輸入框：打字就列出名單上吻合的名字，順便顯示各自還能報幾位。
 *
 * 為什麼不是純文字框：接龍模式下「名字必須跟 LINE 接龍上的一字不差」，
 * 而人們記得的是自己在接龍裡怎麼寫的（暱稱、英文名、有沒有空格），
 * 常常打出一個很接近但對不上的字串，然後看到「不在名單上」卡住。
 * 直接把名單攤在眼前選，這個問題就不存在了。
 *
 * 名單本來就在公開的 flight view 裡（報名端要靠它擋名額），
 * 所以這裡不需要任何新的 API —— 純粹是把已經拿到的資料呈現出來。
 */
export function InvitePicker({
  invites,
  value,
  onChange,
  placeholder,
}: {
  invites: InviteView[];
  value: string;
  onChange: (name: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const matches = useMemo(() => matchInvites(invites, value), [invites, value]);
  const shown = matches.slice(0, MAX_SUGGESTIONS);
  const hidden = matches.length - shown.length;

  // 已經打出完全吻合的名字時就不用再擋著畫面了
  const exact = matches.length === 1 && matches[0].name.toLowerCase() === value.trim().toLowerCase();
  const listOpen = open && shown.length > 0 && !exact;

  const pick = (name: string) => {
    onChange(name);
    setOpen(false);
    setActive(0);
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!listOpen) {
      // 關著的時候按下箭頭 = 想看名單
      if (e.key === "ArrowDown") {
        setOpen(true);
        setActive(0);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      setActive((i) => (i + 1) % shown.length);
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setActive((i) => (i - 1 + shown.length) % shown.length);
      e.preventDefault();
    } else if (e.key === "Enter") {
      // 選單開著時的 Enter 是「選這個」，不是「送出表單」
      e.preventDefault();
      pick(shown[active].name);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        className="field-input"
        placeholder={placeholder}
        maxLength={30}
        required
        autoFocus
        autoComplete="off"
        // 固定的 aria-label：placeholder 是會隨文案調整的東西，
        // 但這個欄位「是姓名欄」不會變 —— 冒煙測試靠它定位
        aria-label="姓名"
        role="combobox"
        aria-expanded={listOpen}
        aria-controls="invite-suggestions"
        aria-autocomplete="list"
        aria-activedescendant={listOpen ? `invite-option-${active}` : undefined}
      />

      {listOpen && (
        <ul
          id="invite-suggestions"
          role="listbox"
          className="border-line bg-card absolute z-20 mt-1.5 max-h-80 w-full overflow-y-auto rounded-xl border py-1 shadow-lg"
          // mousedown 會先於 blur 發生，不擋的話 input 一失焦選單就關了，click 永遠收不到
          onMouseDown={(e) => e.preventDefault()}
        >
          {shown.map((i, idx) => {
            const left = remainingOf(i);
            return (
              <li key={i.id} id={`invite-option-${idx}`} role="option" aria-selected={idx === active}>
                <button
                  type="button"
                  onClick={() => pick(i.name)}
                  onMouseEnter={() => setActive(idx)}
                  className={`flex w-full items-center justify-between gap-3 px-3.5 py-2 text-left text-[15px] ${
                    idx === active ? "bg-inset" : ""
                  }`}
                >
                  <span className={left === 0 ? "text-sub" : "font-medium"}>{i.name}</span>
                  <span className={`shrink-0 text-[12px] ${left === 0 ? "text-sub" : "text-ok-deep"}`}>
                    {left === 0 ? "已額滿" : `還可報 ${left} 位`}
                  </span>
                </button>
              </li>
            );
          })}
          {hidden > 0 && (
            <li className="text-sub px-3.5 py-2 text-[12px]">
              還有 {hidden} 個名字——再多打一個字就找得到
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
